import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useFormik } from "formik";
import { StrategySchema } from "./Schema";
import { TAGS_OPTIONS, CATEGORIES_OPTIONS } from "./constants";
import { useSelector } from "react-redux";
import {
  selectStrategies,
  selectManagers,
} from "../../Redux/Reducers/selectors";

const filter = createFilterOptions();

const sameStringArray = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

const StrategiesDialog = ({ open, onClose, strategy, onSave }) => {
  const managers = useSelector(selectManagers);
  const strategies = useSelector(selectStrategies);
  const [formats, setFormats] = useState(["personal"]);

  const isEdit = !!strategy;

  useEffect(() => {
    if (isEdit && strategy) {
      if (strategy.owner?.includes("manager#")) {
        setFormats(["manager"]);
      } else {
        setFormats(["personal"]);
      }
    } else {
      setFormats(["personal"]);
    }
  }, [strategy, isEdit, open]);

  const handleFormat = (event, newFormat) => {
    if (newFormat !== null) {
      setFormats([newFormat]);
    }
  };

  const initialValues = {
    name: strategy?.name || "",
    description: strategy?.description || "",
    tags: strategy?.metadata?.tags || strategy?.tags || [],
    categories: strategy?.metadata?.categories || strategy?.categories || [],
  };

  const formik = useFormik({
    initialValues,
    validationSchema: StrategySchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const duplicateStrategy = strategies?.find(
        (s) =>
          s.name.toLowerCase() === values.name.toLowerCase() &&
          (!isEdit || s.strategyId !== strategy.strategyId)
      );

      if (duplicateStrategy) {
        alert(`${values.name} already exists, try a new one.`);
        return;
      }

      if (!isEdit) {
        const requestData = { ...values };
        if (formats[0] === "manager") {
          requestData.owner = `manager#${managers[0]?.managerId}`;
        }
        onSave(requestData);
        return;
      }

      // Edit: send only changed keys
      const requestData = {};

      const baselineDescription = strategy.description || "";
      if (values.description !== baselineDescription) {
        requestData.description = values.description;
      }

      const baselineTags = strategy?.metadata?.tags || strategy?.tags || [];
      if (!sameStringArray(values.tags, baselineTags)) {
        requestData.tags = values.tags;
      }

      const baselineCategories =
        strategy?.metadata?.categories || strategy?.categories || [];
      if (!sameStringArray(values.categories, baselineCategories)) {
        requestData.categories = values.categories;
      }

      const nextOwner =
        formats[0] === "manager"
          ? `manager#${managers[0]?.managerId}`
          : null;
      if (nextOwner && strategy.owner !== nextOwner) {
        requestData.owner = nextOwner;
      }

      onSave(requestData);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setFormats(["personal"]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
            <TrendingUpIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 , fontSize: "20px"}}>
              {isEdit ? "Edit Strategy" : "Add Strategy"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? "Update strategy details"
                : "Create a new trading strategy"}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              name="name"
              label="Strategy Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              disabled={isEdit}
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={formik.touched.description && formik.errors.description}
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
            />

            <Autocomplete
              multiple
              freeSolo
              options={TAGS_OPTIONS}
              value={formik.values.tags}
              onChange={(_, newValue) => {
                formik.setFieldValue(
                  "tags",
                  newValue.map((v) =>
                    typeof v === "string" ? v : v.inputValue || v
                  )
                );
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(
                  (option) => inputValue === option
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    title: `Add "${inputValue}"`,
                  });
                }
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return option.title || option;
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.title || option}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags"
                  helperText="Choose from the list or type a custom tag and press Enter"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                />
              )}
            />

            <Autocomplete
              multiple
              freeSolo
              options={CATEGORIES_OPTIONS}
              value={formik.values.categories}
              onChange={(_, newValue) => {
                formik.setFieldValue(
                  "categories",
                  newValue.map((v) =>
                    typeof v === "string" ? v : v.inputValue || v
                  )
                );
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(
                  (option) => inputValue === option
                );
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    inputValue,
                    title: `Add "${inputValue}"`,
                  });
                }
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return option.title || option;
              }}
              renderOption={(props, option) => (
                <li {...props}>{option.title || option}</li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categories"
                  placeholder="Add categories"
                  helperText={
                    "Choose from the list or type a custom category and press Enter"
                  }
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                />
              )}
            />

            <FormControl fullWidth>
              <InputLabel shrink sx={{ fontWeight: 600, mb: 1 }}>
                Strategy Owner
              </InputLabel>
              <ToggleButtonGroup
                value={formats[0]}
                exclusive
                onChange={handleFormat}
                aria-label="strategy type"
                sx={{
                  mt: 2,
                  "& .MuiToggleButton-root": {
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                  },
                }}
              >
                <ToggleButton value="personal" aria-label="personal">
                  Personal
                </ToggleButton>
                <ToggleButton value="manager" aria-label="manager">
                  Manager
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 120,
                }}
              >
                {isEdit ? "Save" : "Create Strategy"}
              </Button>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StrategiesDialog;
